import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { TslToastrService } from '@tesla/ng-tds';
import { OnboardingContactsComponent } from 'app/common/onboarding-contacts/onboarding-contacts.component';
import { GeneralSettings } from 'app/profile/models/generic.model';
import { Country, Role } from 'app/profile/models/masterdata.model';
import { OnboardingTab, OnboardingDocument, OnboardingTabStatus, TaskStatus } from 'app/profile/models/onboarding.model';
import { ContextVerbiage, Language, OPERATION, SupplierContact } from 'app/profile/models/supplier.model';
import { GetSupplierProfileCurrentUser } from 'app/profile/store/app.actions';
import { SelectCountries, SelectCurrentUser } from 'app/profile/store/app.selector';
import { OnboardingState } from 'app/profile/store/state/onboarding.state';
import { SupplierState } from 'app/profile/store/state/supplierstate.state';
import { I18nService } from 'app/shared/services/i18n.service';
import { Observable, Subject, Subscription, combineLatest } from 'rxjs';
import { ContactGroupModel, OnboardingContactType, OnboardingContactTabData } from '../../onboarding/contacts-wrapper/contact-group.model';
import * as MasterDataActions from "../../../store/actions/masterdata.action";
import * as MasterDataSelectors from "../../../store/selectors/masterdata.selector";
import * as GeneralActions from "../../../store/actions/general.action";
import * as GeneralSelectors from "../../../store/selectors/general.selector";
import { SupplierTasksState } from 'app/profile/store/state/supplier-tasks.state';
import * as SupplierTasksActions from "../../../store/actions/supplier-task.action";
import * as SupplierTasksSelectors from "../../../store/selectors/supplier-tasks.selector";
import * as OnboardingActions from '../../../store/actions/onboarding.actions';
import { SupplierTaskResponse } from 'app/profile/models/suppliertasks.model';
import { SupplierTaskService } from 'app/profile/services/supplier-task.service';
import { OnboardingService } from 'app/profile/services/onboarding.service';
import { PascalCasePipe } from 'app/shared/pipes/splitString.pipe';
import { OnboardingRequestType } from 'app/profile/enums/onboarding-request';
import { isNullOrUndefined } from 'util';

@Component({
  selector: 'app-contact-task-wrapper',
  templateUrl: './contact-task-wrapper.component.html',
  styleUrls: ['./contact-task-wrapper.component.scss'],
  providers: [PascalCasePipe]
})
export class ContactTaskWrapperComponent implements OnInit {

  //  This component is for the contacts tab
  onboardingTab: OnboardingTab = OnboardingTab.CONTACTS;

  /** Event to trigger once the entire process of creating the contacts is completed */
  @Output('tsl-onComplete') onCompleteEvent: EventEmitter<void> = new EventEmitter<void>();

  //  Get reference to contact edit form
  @ViewChildren(OnboardingContactsComponent) contactComponents!: QueryList<OnboardingContactsComponent>;
  //  Fields required by the contact-edit form
  //  They are from the supplier store
  settings$: Observable<GeneralSettings>;
  languages$: Observable<Language[]>;
  countries$: Observable<Country[]>;
  roles$: Observable<Role[]>;

  requiredEmailDomains: string[] = [];
  ngUnsubscribe = new Subject<void>();
  onboardingVendorCountry: string;
  contextVerbiage: ContextVerbiage;
  currentTabStatus: string;
  isIncompleteTabs: boolean = true;
  pcfTemplateID: string;
  missingTabs: any;
  sameAsCurrenUser: boolean = true;
  isDocumentRequired: boolean = false;
  isDocumentUploaded: boolean = false;
  invalidEmailDomains: boolean = false;
  invalidEmailDomainsMessage: string = "";

  subscriptions: Subscription[] = [];
  contactGroupModel: ContactGroupModel = {
    // current logged in user
    [OnboardingContactType.MYDETAILS]: { type: OnboardingContactType.MYDETAILS, required: true, sameAsPrimary: false, sameAsCurrentUser: false, visited: false, contact: null, displayIsPrimaryOption: true, additionalContactTypes: [], isContactCopied: false },
    //  Primary type requires a new object, set visited to true because that's the starting tab that the user will be in
    [OnboardingContactType.PRIMARY]: { type: OnboardingContactType.PRIMARY, required: true, sameAsPrimary: false, sameAsCurrentUser: false, visited: false, contact: null, displayIsPrimaryOption: false, additionalContactTypes: [], isContactCopied: false },

    //  Sales, Finance Lead, and RemitTo contacts are not required to create a new contact object, they can use the same as primary
    [OnboardingContactType.SALES]: { type: OnboardingContactType.SALES, required: false, sameAsPrimary: false, sameAsCurrentUser: false, visited: false, contact: null, displayIsPrimaryOption: false, additionalContactTypes: [], isContactCopied: false },
    [OnboardingContactType.FINANCE]: { type: OnboardingContactType.FINANCE, required: false, sameAsPrimary: false, sameAsCurrentUser: false, visited: false, contact: null, displayIsPrimaryOption: false, additionalContactTypes: [], isContactCopied: false },
    [OnboardingContactType.REMMITANCE]: { type: OnboardingContactType.REMMITANCE, required: false, sameAsPrimary: false, sameAsCurrentUser: false, visited: false, contact: null, displayIsPrimaryOption: false, additionalContactTypes: [], isContactCopied: false },
    [OnboardingContactType.CRP]: { type: OnboardingContactType.CRP, required: false, sameAsPrimary: false, sameAsCurrentUser: false, visited: false, contact: null, displayIsPrimaryOption: false, additionalContactTypes: [], isContactCopied: false },
    [OnboardingContactType.EHS]: { type: OnboardingContactType.EHS, required: false, sameAsPrimary: false, sameAsCurrentUser: false, visited: false, contact: null, displayIsPrimaryOption: false, additionalContactTypes: [], isContactCopied: false },
    //  Material planning contact requires a new object, so it can't be the same as the primary contact
    [OnboardingContactType.MATERIAL_PLANNING]: { type: OnboardingContactType.MATERIAL_PLANNING, required: false, sameAsPrimary: false, sameAsCurrentUser: false, visited: false, contact: null, displayIsPrimaryOption: false, additionalContactTypes: [], isContactCopied: false },

    // sub tier contacts
    [OnboardingContactType.SUBTIERPROFILEADMIN]: { type: OnboardingContactType.SUBTIERPROFILEADMIN, required: false, sameAsPrimary: false, sameAsCurrentUser: false, visited: false, contact: null, displayIsPrimaryOption: false, additionalContactTypes: [], isContactCopied: false },
    [OnboardingContactType.SUBTIERQUALITY]: { type: OnboardingContactType.SUBTIERQUALITY, required: false, sameAsPrimary: false, sameAsCurrentUser: false, visited: false, contact: null, displayIsPrimaryOption: false, additionalContactTypes: [], isContactCopied: false }
  };

  /**
   * Array of data that each tabs requires in the contacts tabs group.
   * Used to iterate in the template the contact types in the contact group model.
   */
  contactTabsData: OnboardingContactTabData[] = [];

  supplierContacts: SupplierContact[] = [];
  requiredContactTypes: string[] = [];
  allowedDomains: string[] = [];
  requiredDocuments: OnboardingDocument[];
  existingContacts: any[] = [];
  supplierCode: string = null;
  taskId: number;
  requestId: string;
  rejectionDetails: any;
  isLoaded: boolean = false;
  taskStatus: string;
  onboardingRequestType: string;
  SupplierContact = SupplierContact;

  @Input('supplierCode') set _supplierCode(val: string) { if (val && val.trim() != "") this.supplierCode = val; };
  @Input('taskId') set _taskId(val: number) { if (val) this.taskId = val; };
  @Input('requestId') set _requestId(val: string) { if (val) {this.requestId = val;} else {this.requestId = ''} };
  @Input('isSidePanelMode') isSidePanelMode: boolean = false;
  @Output() onSave: EventEmitter<boolean> = new EventEmitter<boolean>();

  constructor(
    private matDialog: MatDialog,
    private supplierStore: Store<SupplierState>,
    private translate: I18nService,
    private tasksStore: Store<SupplierTasksState>,
    private route: ActivatedRoute,
    protected onboardingStore: Store<OnboardingState>,
    private taskService: SupplierTaskService,
    private toaster: TslToastrService,
    private router: Router,
    private service: OnboardingService,
    private pascalCase: PascalCasePipe,
  ) { }

  ngOnInit(): void {
    this.route?.parent?.params.subscribe(params => {
      if (params?.code && params?.portalTaskId) {
        this.supplierCode = params.code;
        this.taskId = params.portalTaskId;
      }
    });

    //  Dispatch action to get language list, role list and step status
    this.supplierStore.dispatch(new MasterDataActions.GetLanguageList());
    this.supplierStore.dispatch(new MasterDataActions.GetContactRoleList());
    this.supplierStore.dispatch(new GetSupplierProfileCurrentUser());
    this.supplierStore.dispatch(new MasterDataActions.GetCountryList());
    // this.tasksStore.dispatch(new SupplierTasksActions.GetTaskStepState(this.supplierCode, this.taskId, this.onboardingTab, true));

    this.languages$ = this.supplierStore.select(MasterDataSelectors.SelectLanguages);
    this.countries$ = this.supplierStore.select(MasterDataSelectors.SelectCountries);
    this.roles$ = this.supplierStore.select(MasterDataSelectors.SelectContactRoles);
    this.onboardingStore.select(MasterDataSelectors.SelectContextVerbiage).subscribe(contextVerbiage => this.contextVerbiage = contextVerbiage);

    combineLatest([this.supplierStore.select(SelectCurrentUser), this.taskService.getSupplierTaskStep(this.supplierCode, this.taskId, this.onboardingTab), this.service.getContactsBySupplierCode(this.supplierCode, this.taskId)
    ])
      .subscribe(([currentUser, taskInfo, existingContacts]) => {
        if (currentUser && taskInfo && taskInfo.taskConfiguration) {
          this.onboardingVendorCountry = taskInfo.taskConfiguration?.VendorCountry;
          this.requiredContactTypes = taskInfo.taskConfiguration.RequiredContactTypes;
          this.requiredDocuments = taskInfo.taskConfiguration?.Documents;
          this.allowedDomains = taskInfo.taskConfiguration?.AllowedDomains;
          this.taskStatus = taskInfo.taskStatus;
          this.rejectionDetails = taskInfo?.rejectionDetails;
          this.onboardingRequestType = taskInfo.taskConfiguration?.SupplierRequestType;
          if (this.taskStatus != TaskStatus.Completed && taskInfo.portalTaskResponse != null) {
            this.supplierContacts = taskInfo.portalTaskResponse;
          }
          else {
            this.supplierContacts = taskInfo.taskConfiguration.SupplierContacts;
          }

          this.existingContacts = this.pascalCase.transform(existingContacts);

          // for existing roles and let the user select the required roles
          if(this.existingContacts && this.existingContacts.length > 0){
            this.existingContacts.forEach(x => x.Types = []);
            if(this.supplierContacts && this.supplierContacts.length > 0){
              this.supplierContacts.forEach(contact => {
                if(contact && contact.Types && contact.Types.length > 0){
                  var selectedContact = this.existingContacts.find(x => x.Email == contact.Email);
                  if(selectedContact){
                    selectedContact.Types = contact.Types
                  }
                }
              })
            }
          }

          if (this.requiredContactTypes && this.requiredContactTypes.length > 0) {
            this.requiredContactTypes.forEach(x => {
              if (this.contactGroupModel[x]) {
                this.contactGroupModel[x].required = true;
                this.contactTabsData.push(this.contactGroupModel[x]);
              }
            });

            let currentUserContact = this.supplierContacts.find(c => c.Email.toLowerCase() == currentUser.supplierContact.emailId.toLowerCase());
            let myDetailsTab = this.contactTabsData.find(ct => ct.type == OnboardingContactType.MYDETAILS);
            myDetailsTab.contact = currentUserContact;
            if (currentUserContact) {
              myDetailsTab.visited = true;
            }

            // Initalize contacts
            this.contactTabsData.forEach(x => {
              this.initializeContact(x.type, currentUserContact);
            });
            this.isIncompleteTabs = this.contactTabsData?.some(x => !x.contact || (x.contact && !(x.contact.JobTitle && x.contact.Phone)));
            let primaryContact = this.supplierContacts?.find(c => c.Types.includes(OnboardingContactType.PRIMARY));
            if(this.allowedDomains && this.allowedDomains.length > 0){
              this.requiredEmailDomains = this.allowedDomains;
            } else if (primaryContact?.Email) {
              //  Set required domain based on the primary contact email
              let email = primaryContact.Email.toLowerCase();
              this.requiredEmailDomains.push(email.substring(email.lastIndexOf("@")));
            } else if(this.supplierContacts?.length && this.supplierContacts[0]?.Email) {
              this.requiredEmailDomains.push(this.supplierContacts[0].Email.substring(this.supplierContacts[0].Email.lastIndexOf("@")));
            }
            this.isDocumentUploaded = primaryContact?.DocumentInfos?.length >= 1 ? true : false;
            if (this.requiredDocuments && this.requiredDocuments.length > 0) {
              this.isDocumentRequired = this.requiredDocuments.length >= 1 && this.onboardingVendorCountry == 'CN' ? true : false;
            }
          }
          this.isLoaded = true;
        }
      });
  }


  /**
   * Handler for the submit event of a single contact from the contact-edit component.
   * It stores the provided supplier contact in the contactGroupModel
   */
  singleContactSubmitHandler(supplierContact: SupplierContact, isContactCopied: boolean, copiedContactType: string, overRideCopiedContact: boolean = false) {
    let type = supplierContact.Types[0];
    let remove = this.contactTabsData?.find(x => x?.contact?.Types?.includes(type) && x?.type != type);
    if (remove) {
      remove.contact.Types = remove.contact.Types.filter(x => x != type);
      if (type == 'Primary') {
        remove.contact.Documents = []
        remove.contact.DocumentInfos = []
      }
    }

    if (isContactCopied) {
      let contactType = supplierContact.Types[0];

      //  Get the contact tab data for the type that we are saving
      let contactTab = this.contactGroupModel[contactType];
      contactTab.visited = true;
      contactTab.isContactCopied = isContactCopied;


      let contact = this.contactTabsData?.find(x => x.type == copiedContactType);
      if (type == OnboardingContactType.PRIMARY) {
        if (supplierContact?.Documents?.length >= 1) {
          contact.contact.DocumentInfos = supplierContact?.DocumentInfos;
          contact.contact.Documents = supplierContact?.Documents;
          this.isDocumentUploaded = supplierContact?.Documents?.length >= 1
          this.setErrorMessage('');
        }
      }

      // remove other references from other contacts
      if (contact && contact.contact && !contact.contact.Types.includes(type)) {
        contact?.contact?.Types?.push(type);
        // set existing contact to null if its copied
        let oldContact = this.contactTabsData.find(x => x.type == contactType);
        oldContact.contact = supplierContact;
      } else if (supplierContact.Types.includes(copiedContactType)) {
        let existingContact = this.existingContacts?.find(x => x.Email == supplierContact.Email);
        existingContact.Types?.push(type);
        supplierContact.Types = existingContact.Types;
        contact.contact = supplierContact;
      }

    // if over ride contact then replace the existing with incoming value
    if(overRideCopiedContact){
      // do there
      this.existingContacts = this.existingContacts.filter(x => x.Email != supplierContact.Email);
      this.existingContacts.push(supplierContact);
      this.contactTabsData.forEach(x => 
        {
        if(x && x.contact && x.contact.Email == supplierContact.Email){
          let oldTypes = x.contact.Types;
          x.contact = supplierContact;
          const mergedArray: string[] = [...x.contact.Types, ...oldTypes];
          const uniqueMergedArray: string[] = [...new Set(mergedArray)];
          x.contact.Types = uniqueMergedArray;
        }
      })
    }
      // recalculate missing tabs
      this.isIncompleteTabs = this.contactTabsData.some(x => !x.contact || (x.contact && !(x.contact.JobTitle && x.contact.Phone)))
      // this.hasUnsavedData = this.isIncompleteTabs;
    }
    else {
      // Before saving, check if the new contact email matches the email domains of the previous contacts
      let contacts = this.contactTabsData.filter(tabData => tabData.contact).map(tabData => tabData.contact);
      contacts.push(supplierContact);

      // for entity extension this is not valid
      if(!isNullOrUndefined(this.requiredEmailDomains) && this.requiredEmailDomains.length > 1){
          // skip single domain check
      }else{
        if (!this.domainsAreEqual(contacts)) {
          //  Exit, domains are not equal
          this.toaster.error('Email domains do not match. Please check and retry.');
          return;
        }
      }

      //  The supplier contact object should only have one type, get the type
      let contactType = supplierContact.Types[0];
      let oldTypes = this.contactTabsData.find(x => x.type.toString() == copiedContactType)?.contact?.Types;

      // check if contact sync is required.
      // Assupmtion: email id mismatch implies new contact.
      let oldEmail = this.contactTabsData.find(x => x.type.toString() == copiedContactType)?.contact?.Email;
      //  Get the contact tab data for the type that we are saving
      let contactTab = this.contactGroupModel[contactType];

      //  Save the supplier contact and document objects to send request later to back-end
      contactTab.contact = supplierContact;
      contactTab.contact.Operation = OPERATION.CREATE;

      if ((this.requiredEmailDomains == null || this.requiredEmailDomains.length == 0) && supplierContact.Email) {
        //  Set required domain if it hasn't been set yet
        let email = supplierContact.Email.toLowerCase();
        this.requiredEmailDomains.push(email.substring(email.lastIndexOf("@")));
      }

      // Check if the user has navigated through all the contact tabs
      //  The user hasn't navigated through the contacts.
      //  Get the missing contact types
      this.isIncompleteTabs = this.contactTabsData.some(x => !x.contact || (x.contact && !(x.contact.JobTitle && x.contact.Phone)))
      // this.hasUnsavedData = this.isIncompleteTabs;
      // update contactTab in contactTabData for that contact
      //  Move to the next tab of contacts. This will also set the visited to true.
      if (contactTab) {
        contactTab.visited = true;
        contactTab.isContactCopied = isContactCopied;
      }

      let primaryTab = this.contactTabsData.find(ct => ct.type == OnboardingContactType.PRIMARY);
      if (contactType == OnboardingContactType.MYDETAILS && primaryTab?.isContactCopied) {
        let tempDocument: string[] = [];
        let tempDocumentInfo: OnboardingDocument[] = [];
        // check if primary already had a PCF if so append it new edited object
        if (primaryTab?.contact?.Documents?.length > 0 && primaryTab?.contact?.DocumentInfos?.length > 0) {
          tempDocumentInfo = primaryTab.contact.DocumentInfos;
          tempDocument = primaryTab.contact.Documents;
        }
        let types = primaryTab.contact.Types;
        primaryTab.contact = supplierContact
        primaryTab.contact.Types = types;
        primaryTab.contact.Documents = tempDocument;
        primaryTab.contact.DocumentInfos = tempDocumentInfo;
      }

      if (contactType == OnboardingContactType.PRIMARY) {
        if (supplierContact?.Documents?.length >= 1) {
          primaryTab.contact.DocumentInfos = supplierContact?.DocumentInfos;
          primaryTab.contact.Documents = supplierContact?.Documents;
          this.isDocumentUploaded = supplierContact?.Documents?.length >= 1
          // this.setErrorMessage('');
        }
      }
      //save back oldtypes
      if (oldTypes && supplierContact.Email == oldEmail && !remove) {
        contactTab.contact.Types = oldTypes;
        //sync info to child contact types
        this.syncCopiedContacts(contactTab.contact);
      }
    }
  }

  openContactEdit(contact: any) {
    var matDataItem: any = {
      supplierCode: this.supplierCode,
      supplierContact: contact.contact,
      readOnly: contact.isContactCopied,
      contactTypes: this.roles$,
      languages: this.languages$,
      generalSettings: this.settings$,
      saveButtonLabel: "Add Next Contact",
      requiredEmailDomain: this.requiredEmailDomains,
      defaultContactTypes: contact.type,
      hideSaveButton: false,
      hideCancelButton: false,
      vendorCountry: this.onboardingVendorCountry,
      cancelButtonLabel: "Back",
      contextVerbiage: this.contextVerbiage,
      requiredDocuments: this.requiredDocuments,
      savedContacts: this.contactTabsData.filter(tab => tab.contact && !tab.isContactCopied).map(x => x.contact),
      contactsTabData: this.contactTabsData,
      showAllContacts: true,
      SupplierOnboardingType: this.onboardingRequestType,
      countries: this.countries$
    }

    // remove duplicate contacts
    var copy = this.existingContacts;
    matDataItem.savedContacts.forEach(c => {
      let index = copy.findIndex(ec => ec.Email.toLowerCase() == c.Email.toLowerCase());
      if (index > -1) {
        copy.splice(index, 1);
      }
    });
    matDataItem.savedContacts = matDataItem.savedContacts.concat(copy);

    if (contact.required) {
      matDataItem.defaultEmailAddress = contact.contact && contact.contact.Email && contact.type === 'My Details' ? contact.contact.Email : null;
      matDataItem.ismarkedPrimary = contact.isContactCopied ? true : (contact.contact ? false : null);
      matDataItem.showIsPrimaryCheckbox = (contact.type === 'Primary' && (this.onboardingRequestType != OnboardingRequestType.Reactivation)) ? true : false,
      matDataItem.requiresDocument = contact.type === 'My Details' && this.requiredDocuments ? contact.type === 'My Details' && this.requiredDocuments : null;
    }
    else {
      matDataItem.defaultEmailAddress = contact.contact && contact.contact.Email && contact.type === 'Primary' ? contact.contact.Email : null;
      matDataItem.requiredDocuments = contact.type == 'Primary' ? this.requiredDocuments : null;
      matDataItem.showIsPrimaryCheckbox = false;
      matDataItem.requiresDocument = false;
    }
    matDataItem.isSidePanelMode = !!this.isSidePanelMode;
    let dialogConfig = this.isSidePanelMode ? {
      panelClass: ['no-padding'],
      height: "890px",
      width: "1000px",
      maxHeight: '80vh',
      data: matDataItem
    } : {
      panelClass: ['sidepanel', 'animate__animated', 'animate__slideInRight'],
      width: '65%',
      height: '100%',
      minWidth: '630px',
      data: matDataItem
    };

    const dialogRef = this.matDialog.open(OnboardingContactsComponent, dialogConfig);

    dialogRef.afterClosed().subscribe((x) => {
      if (x) {
        // 0 - suppliercontact, 1 - isCopied, 2 - copiedType
        this.singleContactSubmitHandler(x[0], x[1], x[2], x[3])
      }
    });

  }

  initializeContact(contactType: string, currentUserContact: SupplierContact) {
    let contact = this.supplierContacts.find(c => c.Types.includes(contactType));
    let contactDetails = this.contactTabsData.find(tab => tab.type == contactType);
    if (contact) {
      if (currentUserContact?.Types?.includes(contactType)) {
        contactDetails.contact = this.taskStatus == TaskStatus.Rejected ? currentUserContact : null;
        contactDetails.isContactCopied = this.taskStatus == TaskStatus.Rejected ? true : false;
      }
      else {
        if(this.onboardingRequestType == OnboardingRequestType.Reactivation){
          if(this.existingContacts && this.existingContacts.filter(x => x.Email == contact.Email)?.length > 0){
            contactDetails.isContactCopied = true;
          }
        }

        if(contactDetails.isContactCopied == false){
          // check if new contact is shared between two roles
          let existingTabContact = this.contactTabsData?.map(x => x.contact)?.find(x => x?.Email == contact.Email);
          if(existingTabContact){
            contactDetails.isContactCopied = true;
          }
        }

        contactDetails.contact = contact;
      }
      contactDetails.visited = true;
    }
  }

  /** Event handler for submitting all contacts to the back-end */
  submit() {
    this.missingTabs = this.contactTabsData.filter(tab => !tab.contact || !SupplierContact.isValid(tab.contact)).map(tab => tab.type);
    this.isIncompleteTabs = this.contactTabsData.some(x => !x.contact || !SupplierContact.isValid(x.contact));
    if (this.isDocumentRequired) {
      if (this.isDocumentUploaded) {
      }
      else {
        this.toaster.error(`${this.translate.get('Contact document is missing')}`);
        return;
      }

    }
    else {
      this.setErrorMessage('');
    }

    if (this.missingTabs.length == 0) {
      // Get all the supplier contact object that we need to save
      let contacts = this.contactTabsData.filter(tab => tab.contact && !tab.isContactCopied);

      let copiedTabContacts = this.contactTabsData.filter(tab => tab.contact && tab.isContactCopied);
      if (copiedTabContacts?.length > 0 && this.existingContacts?.length > 0) {
        copiedTabContacts.forEach(copiedTab => {
          var contactCopiedFromExisting = this.existingContacts.find(ec => ec?.Email == copiedTab.contact?.Email);
          if (contactCopiedFromExisting && !contacts.find(c => c.contact?.Email == contactCopiedFromExisting.Email)) {
            contacts.push(copiedTab);
          }
        });
      }

      // check duplicate email
      const savedContacts = this.contactTabsData.filter(tab => tab.contact && !tab.isContactCopied).map(x => x.contact);
      let duplicateList = savedContacts?.filter((item, index) => savedContacts.findIndex(x => item.Email == x.Email) !== index);
      if(duplicateList?.length) {
        this.toaster.error(`${this.translate.get('Same email has been applied for different users, please check: ')}${[...new Set(duplicateList.map(x => x.Email))].join(", ")}`);
        return;
      }
      // clean up payload for submission to backend
      let payload = this.BuildPayload(contacts);

      var emailIds = payload?.filter(x => x.Email).map(x => x.Email);
      let distictEmailIds = [...new Set(emailIds)]
      if (payload?.length != distictEmailIds?.length) {
        this.toaster.error(`Multiple contacts with same email are not allowed. Please check and try again.`);
      }
      else {
        //  Save contact information
        this.postInformation(payload);
      }
    }
  }

  postInformation(contacts: SupplierContact[]) {
    var response: SupplierTaskResponse = {
      id: this.taskId,
      supplierCode: this.supplierCode,
      taskResponseJson: JSON.stringify(contacts)
    }

    this.taskService.postSupplierTaskStepResponse(this.supplierCode, response).subscribe(res => {
      if (res) {
        this.toaster.success('Submitted successfully!');
        if (this.isSidePanelMode) this.onSave.emit(true);
        else this.router.navigate([`${this.supplierCode}`, "Onboarding", "Home"]);
      }
      else {
        this.toaster.error('Something went wrong!');
      }
    });
  }

  private BuildPayload(contacts: OnboardingContactTabData[]) {
    // remove unwanted roles for submission
    let type = ['Extended Onboarding', 'My Details'];
    let remove = contacts.find(x => x.contact?.Types.find(y => type.includes(y)));
    if (remove) {
      remove.contact.Types = remove.contact.Types.filter(x => !type.includes(x));
    }

    // remove document form data
    let index = contacts.findIndex(x => x.contact?.Types?.includes('Primary'));

    if (index > -1 && contacts[index].contact?.DocumentInfos?.length > 0) {
      contacts[index].contact?.DocumentInfos.map(x => delete x.FormGroup);
    }

    return contacts.map(x => x.contact);
  }
  /** Determines if all the domains entered in the contacts are equal */
  private domainsAreEqual(contacts: SupplierContact[]): boolean {

    //  Validate that all contacts have the same domain
    let emailDomains = contacts.map(con => {
      if (con) {
        let email = con.Email.toLowerCase();
        let domain = email.substring(email.lastIndexOf("@"));
        return domain;
      }
    });

    let firstDomain = emailDomains[0];
    if (!emailDomains.every(domain => domain == firstDomain)) {

      //  Domains are not all equal
      this.invalidEmailDomains = true;
      this.invalidEmailDomainsMessage = `${this.translate.get('Email domains must be the same for every contact. You used:')} ${emailDomains.join(', ')}`;
      // this.setErrorMessage(`${this.translate.get('Email domains must be the same for every contact. You used:')} ${emailDomains.join(', ')}`);
      return false;
    }
    this.invalidEmailDomains = false;
    this.invalidEmailDomainsMessage = ""
    // this.invalidEmailDomains = true;
    return true;
  }

  syncCopiedContacts(supplierContact: SupplierContact) {
    // reflect new information from parent contact to all of its child contacts, if copied
    let types = this.contactTabsData.find(x => x.contact == supplierContact)?.contact?.Types;
    if (types) {
      types.forEach(type => {
        let contact = this.contactTabsData?.find(x => x?.type == type);
        if (contact?.isContactCopied) {
          contact.contact = Object.assign({}, supplierContact);
          contact.contact.Types = [type]
        }
      });
    }
  }

  /** Sets error message at the onboarding store */
  setErrorMessage(errorMessage: string) {
    if (errorMessage) this.onboardingStore.dispatch(OnboardingActions.SetErrorMessage({ errorMessage }));
  }

  navigateToHome() {
    this.router.navigate([`${this.supplierCode}`, "Onboarding", "Home"]);
  }
}
